import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ImagePlus, Link2, Loader2, Pencil, Sparkles, Trash2, X } from "lucide-react";
import { LISTING_ASSET_KINDS } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";

// GORSEL KUTUPHANESI: renk kartelasi, beden tablosu gibi birden fazla listede
// kullanilan ek gorseller. Sablonlar sayfasinda yonetilir; Liste Denetimi'nde
// tasarima, burada listing sablonuna atanir.

export interface ListingAsset { id: string; name: string; kind: string; imageUrl: string; createdAt: string; summary?: string | null; aiNote?: string | null }

export const ASSET_KINDS: Record<string, string> = LISTING_ASSET_KINDS;

const abs = (u?: string | null) => (!u ? "" : /^(https?:|data:|blob:)/.test(u) ? u : `${getBackendUrl()}${u}`);

async function call<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/listing-assets${path}`, {
    method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}
export const assetApi = {
  list: () => call<ListingAsset[]>(""),
  /** kind verilmezse grubu sunucu (AI) secer */
  create: (b: { name: string; kind?: string; image?: string; imageUrl?: string }) => call<ListingAsset>("", "POST", b),
  autoGroup: () => call<{ total: number; changed: number }>("/auto-group", "POST"),
  rename: (id: string, b: { name?: string; kind?: string; summary?: string; aiNote?: string }) => call<ListingAsset>(`/${id}`, "PATCH", b),
  analyze: (id: string, note: string) => call<ListingAsset>(`/${id}/analyze`, "POST", { note }),
  remove: (id: string) => call(`/${id}`, "DELETE"),
  assign: (id: string, b: { designIds?: string[]; listingTemplateId?: string }) =>
    call<{ designs: number; template: boolean }>(`/${id}/assign`, "POST", b),
  unassign: (designId: string, imageUrl: string) => call("/unassign", "POST", { designId, imageUrl }),
  /** Gorsel linki -> kutuphaneye kaydedilir; urun sayfasi -> aday gorseller */
  fromLink: (b: { url: string; name?: string; kind?: string }) =>
    call<{ asset?: ListingAsset; candidates?: string[] }>("/from-link", "POST", b),
};

export interface OptionMatchProposal { name: string; etsyName: string; kind: string; defaultRole: string; values: string[]; note: string }
export const optionMatchApi = {
  suggest: (assetId: string) => call<{ matches: Array<{ setId: string; reasonTr: string }>; proposal: OptionMatchProposal; reasonTr: string }>(`/${assetId}/option-match`, "POST"),
};

/** Secenek Kutuphanesi setleri (gorsel <-> set eslesmesi icin; Sablonlar sayfasiyla ayni sorgu) */
type LibSet = { id: string; name: string; etsyName: string | null; kind: string; defaultRole: string; values: Array<{ label: string }>; chartImageUrl?: string | null; inLibrary?: boolean };
async function optionSetCall<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/option-sets${path}`, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}
function useOptionSets() {
  return useQuery({ queryKey: ["option-sets"], queryFn: async (): Promise<LibSet[]> => {
    const r = await fetch(`${getBackendUrl()}/api/option-sets`, { headers: authHeaders() });
    return r.ok ? (await r.json()).data ?? [] : [];
  } });
}

export function useAssets() {
  return useQuery({ queryKey: ["listing-assets"], queryFn: assetApi.list });
}

/** Kutuphane izgarasi.
 *  - manage: yukle, yeniden adlandir, sil, listing sablonuna ekle
 *  - onPick: verilirse secim modu (Liste Denetimi'nde tasarima eklemek icin) */
export function AssetLibrary({ onPick, pickedUrls, templates }: {
  onPick?: (a: ListingAsset) => void;
  pickedUrls?: string[];
  templates?: Array<{ id: string; name: string }>;
}) {
  const qc = useQueryClient();
  const { data: assets = [], isLoading } = useAssets();
  const [kind, setKind] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadKind, setUploadKind] = useState(""); // "": grubu AI secer
  const [grouping, setGrouping] = useState(false);
  const [analyzing, setAnalyzing] = useState<ListingAsset | null>(null);
  const [matching, setMatching] = useState<ListingAsset | null>(null);
  const { data: optionSets = [] } = useOptionSets();
  const setsOf = (a: ListingAsset) => optionSets.filter((o) => o.chartImageUrl === a.imageUrl);

  const refresh = () => qc.invalidateQueries({ queryKey: ["listing-assets"] });
  const kindOf = (a: ListingAsset) => (a.kind in ASSET_KINDS ? a.kind : "other");
  const count = (k: string) => assets.filter((a) => kindOf(a) === k).length;
  // Bos gruplar sekmede gosterilmez; secili grup bosalirsa Tumu'ne donulur
  const tabs = Object.entries(ASSET_KINDS).filter(([k]) => count(k) > 0);
  const activeKind = kind && count(kind) > 0 ? kind : "";
  const sections = (activeKind ? tabs.filter(([k]) => k === activeKind) : tabs)
    .map(([k, l]) => ({ k, l, list: assets.filter((a) => kindOf(a) === k) }));

  async function autoGroup() {
    setGrouping(true); setMsg(null);
    try {
      const r = await assetApi.autoGroup();
      setMsg(`${r.total} görsel incelendi, ${r.changed} tanesinin grubu değişti.`);
      refresh();
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); } finally { setGrouping(false); }
  }

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setMsg(null);
    try {
      for (const f of Array.from(files).slice(0, 10)) {
        const image = await new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f); });
        await assetApi.create({ name: f.name.replace(/\.[^.]+$/, ""), kind: uploadKind || undefined, image });
      }
      refresh();
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-gray-100 p-0.5 text-xs">
          {[{ k: "", l: "Tümü", n: assets.length }, ...tabs.map(([k, l]) => ({ k, l, n: count(k) }))].map(({ k, l, n }) => (
            <button key={k} onClick={() => setKind(k)} aria-pressed={activeKind === k}
              className={`rounded-md px-2.5 py-1 ${activeKind === k ? "bg-white font-medium text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {l} <span className="text-gray-400">{n}</span>
            </button>
          ))}
        </div>
        {!onPick && (
          <div className="ml-auto flex items-center gap-2 text-xs">
            <button onClick={() => void autoGroup()} disabled={grouping || assets.length === 0}
              title="Tüm görsellerin grubunu AI görsele bakarak yeniden seçer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {grouping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {grouping ? "Gruplanıyor…" : "AI ile grupla"}
            </button>
            <select value={uploadKind} onChange={(e) => setUploadKind(e.target.value)} aria-label="Yüklenecek görselin grubu"
              className="rounded-lg border border-gray-200 px-2 py-1.5 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              <option value="">Grubu AI seçsin</option>
              {Object.entries(ASSET_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { void upload(e.target.files); e.target.value = ""; }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Görsel yükle
            </button>
          </div>
        )}
      </div>
      {msg && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{msg}</p>}

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : assets.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">
          Kütüphane boş. Custom Tasarım kartında kaydedilen kartelalar buraya düşer; ya da görsel yükleyin.
        </p>
      ) : (
        <div className="space-y-5">
        {sections.map(({ k, l, list }) => (
        <section key={k}>
          {!activeKind && (
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
              {l} <span className="text-xs font-normal text-gray-500">{list.length}</span>
            </h2>
          )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {list.map((a) => {
            const picked = pickedUrls?.includes(a.imageUrl);
            return (
              <div key={a.id} className={`overflow-hidden rounded-lg border bg-white ${picked ? "border-emerald-400" : "border-gray-200"}`}>
                <button onClick={() => onPick?.(a)} disabled={!onPick || picked}
                  className={`relative block aspect-square w-full bg-gray-50 ${onPick && !picked ? "cursor-pointer hover:opacity-90" : "cursor-default"}`}>
                  <img src={abs(a.imageUrl)} alt="" loading="lazy" className="h-full w-full object-contain" />
                  {picked && <span className="absolute right-1 top-1 rounded-full bg-emerald-500 p-0.5 text-white"><Check className="h-3 w-3" /></span>}
                </button>
                <div className="space-y-1 p-2 text-xs">
                  {editing?.id === a.id ? (
                    <form className="flex gap-1" onSubmit={(e) => {
                      e.preventDefault();
                      void assetApi.rename(a.id, { name: editing.name }).then(refresh);
                      setEditing(null);
                    }}>
                      <input autoFocus value={editing.name} onChange={(e) => setEditing({ id: a.id, name: e.target.value })}
                        className="min-w-0 flex-1 rounded border border-gray-200 px-1.5 py-0.5" />
                      <button type="button" onClick={() => setEditing(null)} className="text-gray-400"><X className="h-3.5 w-3.5" /></button>
                    </form>
                  ) : (
                    <p className="truncate font-medium text-gray-800" title={a.name}>{a.name}</p>
                  )}
                  {!onPick && (
                    <button onClick={() => setMatching(a)} title="Seçenek Kütüphanesi'ndeki setle eşleştir ya da yeni set oluştur"
                      className={`flex w-full items-center gap-1 truncate text-left text-xs ${setsOf(a).length ? "text-indigo-700 hover:underline" : "text-gray-500 hover:text-indigo-700"}`}>
                      <Link2 className="h-3 w-3 shrink-0" />
                      <span className="truncate">{setsOf(a).length ? `Seçenek: ${setsOf(a).map((o) => o.name).join(", ")}` : "Seçeneğe bağla"}</span>
                    </button>
                  )}
                  {!onPick && (
                    <button onClick={() => setAnalyzing(a)} title="AI analizini gör, düzenle, yeniden analiz et"
                      className={`line-clamp-2 w-full text-left text-xs hover:text-indigo-700 ${a.summary ? "text-gray-500" : "italic text-gray-500"}`}>
                      {a.summary || "Analiz yok — analiz et"}
                    </button>
                  )}
                  {onPick ? (
                    <p className="text-xs text-gray-500">{ASSET_KINDS[kindOf(a)]}</p>
                  ) : (
                    <select value={kindOf(a)} aria-label="Grup" title="Grubu değiştir"
                      onChange={(e) => void assetApi.rename(a.id, { kind: e.target.value }).then(refresh)}
                      className="-ml-1 max-w-full rounded-lg border border-transparent bg-transparent px-1 py-0.5 text-xs text-gray-500 hover:border-gray-200 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                      {Object.entries(ASSET_KINDS).map(([kk, ll]) => <option key={kk} value={kk}>{ll}</option>)}
                    </select>
                  )}
                  {!onPick && (
                    <div className="flex items-center gap-1">
                      {templates && templates.length > 0 && (
                        <select defaultValue="" title="Bu şablonla açılan TÜM listelere eklenir"
                          onChange={(e) => {
                            const id = e.target.value; e.target.value = "";
                            if (!id) return;
                            void assetApi.assign(a.id, { listingTemplateId: id }).then((r) => {
                              const t = templates.find((x) => x.id === id)?.name ?? "";
                              setMsg(r.template ? `"${a.name}" → "${t}" şablonuna eklendi` : `"${t}" şablonunda zaten var`);
                              void qc.invalidateQueries({ queryKey: ["listing-templates"] });
                            });
                          }}
                          className="min-w-0 flex-1 rounded border border-gray-200 px-1 py-0.5 text-[11px]">
                          <option value="">Şablona ekle…</option>
                          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      )}
                      <button onClick={() => setAnalyzing(a)} title="AI analizi" aria-label="AI analizi" className="rounded p-1 text-gray-400 hover:text-indigo-600"><Sparkles className="h-3 w-3" /></button>
                      <button onClick={() => setEditing({ id: a.id, name: a.name })} title="Yeniden adlandır" aria-label="Yeniden adlandır" className="rounded p-1 text-gray-400 hover:text-gray-700"><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => { if (confirm("Kütüphaneden kaldırılsın mı? (Atanmış listelerde kalır)")) void assetApi.remove(a.id).then(refresh); }}
                        title="Kütüphaneden kaldır" aria-label="Kütüphaneden kaldır" className="rounded p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </section>
        ))}
        </div>
      )}
      {analyzing && <AnalysisModal asset={analyzing} onClose={() => setAnalyzing(null)} onSaved={refresh}
        linkedLabel={setsOf(analyzing).map((o) => o.name).join(", ")}
        onMatch={() => { setMatching(analyzing); setAnalyzing(null); }} />}
      {matching && <OptionMatchModal asset={matching} sets={optionSets} onClose={() => setMatching(null)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ["option-sets"] })} />}
    </div>
  );
}

/** Tek gorselin AI analizi: grup + ozet gorulur/duzenlenir; ek notla yeniden analiz */
export function AnalysisModal({ asset, onClose, onSaved, onMatch, linkedLabel }: {
  asset: ListingAsset; onClose: () => void; onSaved: () => void;
  /** Verilirse altta "Seçeneğe bağla" (eşleştirme penceresini açar) */
  onMatch?: () => void; linkedLabel?: string;
}) {
  const [kind, setKind] = useState(asset.kind in ASSET_KINDS ? asset.kind : "other");
  const [summary, setSummary] = useState(asset.summary ?? "");
  const [note, setNote] = useState(asset.aiNote ?? "");
  const [busy, setBusy] = useState<"save" | "analyze" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function run(mode: "save" | "analyze") {
    setBusy(mode); setErr(null);
    try {
      if (mode === "analyze") {
        const r = await assetApi.analyze(asset.id, note);
        setKind(r.kind in ASSET_KINDS ? r.kind : "other"); setSummary(r.summary ?? "");
      } else {
        await assetApi.rename(asset.id, { kind, summary, aiNote: note });
        onClose();
      }
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  }

  const field = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="AI analizi" className="w-full max-w-2xl rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Sparkles className="h-4 w-4 text-indigo-600" />AI analizi · <span className="truncate font-normal text-gray-600">{asset.name}</span></h3>
          <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid gap-4 p-5 sm:grid-cols-[200px_1fr]">
          <img src={abs(asset.imageUrl)} alt="" className="aspect-square w-full rounded-lg border border-gray-200 bg-gray-50 object-contain" />
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">Grup</span>
              <select value={kind} onChange={(e) => setKind(e.target.value)} className={field}>
                {Object.entries(ASSET_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">Özet (liste analizine bu gider)</span>
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} placeholder="Henüz analiz edilmedi" className={field} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">AI'ya ek not</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Ör: Numaralar 1-28, harf kolyesi için; okunuşlar yalnız yardımcı" className={field} />
              <span className="mt-1 block text-xs text-gray-500">Yeniden analizde ve "AI ile grupla"da her seferinde dikkate alınır.</span>
            </label>
            {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {onMatch && (
            <button onClick={onMatch} disabled={!!busy} title="Seçenek Kütüphanesi'ndeki setle eşleştir ya da yeni set oluştur"
              className="mr-auto inline-flex min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
              <Link2 className="h-4 w-4 shrink-0" /><span className="truncate">{linkedLabel ? `Seçenek: ${linkedLabel}` : "Seçeneğe bağla"}</span>
            </button>
          )}
          <button onClick={() => void run("analyze")} disabled={!!busy}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {busy === "analyze" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {asset.summary || summary ? "Yeniden analiz et" : "Analiz et"}
          </button>
          <button onClick={() => void run("save")} disabled={!!busy}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

const MONTHS_RE = /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/gi;
/** Etsy kisisellestirme acilir listesi 20 karakter: uzun degere kisaltma onerisi
 *  (tire bosluklari, ay adlari 3 harf, and -> &, sonra kelime sinirinda kesme) */
export function shortenLabel(v: string, max = 20): string {
  let s = v.trim().replace(/\s*[-–—]\s*/g, "-").replace(/\s+/g, " ");
  if (s.length <= max) return s;
  s = s.replace(MONTHS_RE, (m) => m.slice(0, 3));
  if (s.length <= max) return s;
  s = s.replace(/\band\b/gi, "&").replace(/\b(the|of) /gi, "");
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > max / 2 ? cut.slice(0, sp) : cut).trim();
}

/** Gorsel <-> Secenek Kutuphanesi seti: AI uyan setleri onerir, yoksa gorselden
 *  okunan degerlerle yeni set taslagi hazirlar. Baglamak = setin chart gorseli bu gorsel olur. */
function OptionMatchModal({ asset, sets, onClose, onSaved }: { asset: ListingAsset; sets: LibSet[]; onClose: () => void; onSaved: () => void }) {
  const library = sets.filter((o) => o.inLibrary !== false);
  const linked = library.filter((o) => o.chartImageUrl === asset.imageUrl);
  // Bos setler: baska gorsele bagli set ellenmez (yalniz chart'i olmayanlar onerilir/secilir)
  const empty = library.filter((o) => !o.chartImageUrl);
  const [ai, setAi] = useState<Awaited<ReturnType<typeof optionMatchApi.suggest>> | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [manualId, setManualId] = useState("");
  const [draft, setDraft] = useState<OptionMatchProposal | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function suggest() {
    setAiBusy(true); setErr(null);
    try {
      const r = await optionMatchApi.suggest(asset.id);
      setAi(r); setDraft(r.proposal);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setAiBusy(false); }
  }
  // Bagli set yoksa acilista AI onerisi istenir
  useEffect(() => { if (!linked.length) void suggest(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function link(o: LibSet) {
    setBusy(o.id); setErr(null);
    try {
      await optionSetCall(`/${o.id}`, "PATCH", { chartImageUrl: asset.imageUrl });
      setDone(`"${o.name}" setine bağlandı.`); onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  }
  async function unlink(o: LibSet) {
    setBusy(o.id); setErr(null);
    try {
      await optionSetCall(`/${o.id}`, "PATCH", { chartImageUrl: null });
      setDone(`"${o.name}" setinden ayrıldı.`); onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  }
  async function create() {
    if (!draft) return;
    setBusy("new"); setErr(null);
    try {
      const created = await optionSetCall<LibSet>("", "POST", {
        name: draft.name, etsyName: draft.etsyName || undefined, kind: draft.kind, defaultRole: draft.defaultRole,
        values: draft.kind === "list" ? draft.values.map((label) => ({ label })) : [],
        note: draft.note || undefined, chartImageUrl: asset.imageUrl,
      });
      setDone(`"${created.name}" seti oluşturuldu ve bu görsele bağlandı.`); setDraft(null); onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  }

  const field = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";
  const btn2 = "inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50";
  const setLine = (o: LibSet) => `${o.kind === "list" ? `${o.values.length} değer` : o.kind === "text" ? "metin" : o.kind === "chart" ? "yalnız görsel" : o.kind} · ${o.defaultRole === "variation" ? "varyasyon" : "kişiselleştirme"}`;
  const tooLong = (draft?.values ?? []).map((v, i) => ({ v: v.trim(), i })).filter(({ v }) => v.length > 20);
  const applyShort = (idx: number[]) => draft && setDraft({ ...draft, values: draft.values.map((v, i) => (idx.includes(i) ? shortenLabel(v) : v)) });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Seçeneğe bağla" className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900"><Link2 className="h-4 w-4 shrink-0 text-indigo-600" />Seçeneğe bağla · <span className="truncate font-normal text-gray-600">{asset.name}</span></h3>
          <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid gap-4 overflow-y-auto p-5 sm:grid-cols-[180px_1fr]">
          <img src={abs(asset.imageUrl)} alt="" className="aspect-square w-full rounded-lg border border-gray-200 bg-gray-50 object-contain" />
          <div className="min-w-0 space-y-4">
            {done && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{done}</p>}
            {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}

            {linked.length > 0 && (
              <section>
                <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Bağlı set</h4>
                {linked.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                    <div className="min-w-0"><p className="truncate text-sm font-medium text-gray-900">{o.name}{o.etsyName ? <span className="font-normal text-gray-500"> → {o.etsyName}</span> : null}</p><p className="text-xs text-gray-500">{setLine(o)}</p></div>
                    <button onClick={() => void unlink(o)} disabled={!!busy} className={btn2}>{busy === o.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Bağlantıyı kaldır</button>
                  </div>
                ))}
              </section>
            )}

            <section>
              <div className="mb-1.5 flex items-center justify-between">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">AI önerisi</h4>
                <button onClick={() => void suggest()} disabled={aiBusy} className={btn2}>
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{ai ? "Yeniden öner" : "Öner"}
                </button>
              </div>
              {aiBusy && !ai && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin text-indigo-600" />Görsel ve kütüphane inceleniyor…</p>}
              {ai && (
                <div className="space-y-2">
                  {ai.reasonTr && <p className="text-xs text-gray-500">{ai.reasonTr}</p>}
                  {ai.matches.length === 0 && <p className="text-sm text-gray-700">Kütüphanede bu görsele uyan set yok — aşağıdan yeni set oluşturabilirsiniz.</p>}
                  {ai.matches.map((m) => {
                    const o = empty.find((x) => x.id === m.setId);
                    if (!o) return null;
                    const isLinked = o.chartImageUrl === asset.imageUrl;
                    return (
                      <div key={m.setId} className="flex items-start justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{o.name}{o.etsyName ? <span className="font-normal text-gray-500"> → {o.etsyName}</span> : null}</p>
                          <p className="text-xs text-gray-500">{setLine(o)}</p>
                          {m.reasonTr && <p className="mt-0.5 text-xs text-gray-600">{m.reasonTr}</p>}
                        </div>
                        {isLinked ? <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Bağlı</span> : (
                          <button onClick={() => void link(o)} disabled={!!busy}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                            {busy === o.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Bağla
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section>
              <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Başka bir sete bağla <span className="normal-case tracking-normal text-gray-400">(yalnız görseli olmayan setler)</span></h4>
              <div className="flex gap-2">
                <select value={manualId} onChange={(e) => setManualId(e.target.value)} aria-label="Set seç" className={field}>
                  <option value="">Set seç…</option>
                  {empty.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <button onClick={() => { const o = empty.find((x) => x.id === manualId); if (o) void link(o); }} disabled={!manualId || !!busy} className={btn2}>Bağla</button>
              </div>
            </section>

            {draft && (
              <section className="rounded-lg border border-gray-200 p-3">
                <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Yeni set oluştur (AI taslağı — düzenleyebilirsiniz)</h4>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="block"><span className="mb-1 block text-xs font-medium text-gray-700">Ad (panelde)</span>
                    <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={field} /></label>
                  <label className="block"><span className="mb-1 block text-xs font-medium text-gray-700">Etsy başlığı</span>
                    <input value={draft.etsyName} maxLength={45} onChange={(e) => setDraft({ ...draft, etsyName: e.target.value })} className={field} /></label>
                  <label className="block"><span className="mb-1 block text-xs font-medium text-gray-700">Tip</span>
                    <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })} className={field}>
                      <option value="list">Liste (seçenekler)</option><option value="text">Metin</option><option value="chart">Görsel (seçim yok)</option>
                    </select></label>
                  <label className="block"><span className="mb-1 block text-xs font-medium text-gray-700">Rol</span>
                    <select value={draft.defaultRole} onChange={(e) => setDraft({ ...draft, defaultRole: e.target.value })} disabled={draft.kind === "chart"} className={field}>
                      <option value="personalization">Kişiselleştirme</option><option value="variation">Varyasyon</option>
                    </select></label>
                </div>
                {draft.kind === "list" && (
                  <label className="mt-2 block"><span className="mb-1 block text-xs font-medium text-gray-700">Değerler (her satır bir değer · {draft.values.length}/30)</span>
                    <textarea value={draft.values.join("\n")} rows={6} onChange={(e) => setDraft({ ...draft, values: e.target.value.split("\n").map((v) => v.trimStart()).slice(0, 30) })} className={`${field} font-mono`} />
                    {tooLong.length > 0 && (
                      <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2">
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-red-700">{tooLong.length} değer 20 karakteri aşıyor — kişiselleştirme açılır listesinde kullanılamaz.</p>
                          <button type="button" onClick={() => applyShort(tooLong.map((t) => t.i))}
                            className="shrink-0 rounded-lg border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100">Tümüne uygula</button>
                        </div>
                        <ul className="space-y-1">
                          {tooLong.map(({ v, i }) => (
                            <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                              <span className="font-mono text-red-600">{v} <span className="text-red-500">({v.length})</span></span>
                              <span className="text-gray-400">→</span>
                              <span className="font-mono text-gray-900">{shortenLabel(v)} <span className="text-gray-500">({shortenLabel(v).length})</span></span>
                              <button type="button" onClick={() => applyShort([i])} className="text-indigo-700 hover:underline">Uygula</button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </label>
                )}
                <label className="mt-2 block"><span className="mb-1 block text-xs font-medium text-gray-700">Alıcıya not (ops.)</span>
                  <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} className={field} /></label>
                <div className="mt-3 flex justify-end">
                  <button onClick={() => void create()} disabled={!!busy || !draft.name.trim() || (draft.kind === "list" && !draft.values.some((v) => v.trim()))}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                    {busy === "new" && <Loader2 className="h-4 w-4 animate-spin" />}Oluştur ve bağla
                  </button>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
